import { useState } from "react";
import { RefreshCw, FlagTriangleRight, CheckSquare, Trophy } from "lucide-react";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { ProgressGoalCards } from "@/components/home/ProgressGoalCards/ProgressGoalCards";
import { HEALTH_AREA_MAPPING } from "@/seed/HealthAreaMapping";
import { ProgressNotifications } from "@/components/home/ProgressNotifications/ProgressNotifications";
import { ProgressAI } from "@/components/home/ProgressAI/ProgressAI";
import { CalendarClock, BarChart3 } from "lucide-react";
import { ProgressCalendar } from "@/components/home/ProgressCalendar/ProgressCalendar";
import { ProgressStreaksDemo } from "@/components/home/ProgressStreaksDemo/ProgressStreaksDemo";
import { CategoryBadge } from "@/components/ui/CategoryBadge";
import type { HealthArea } from "instantdb/types";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

// Category data mapped from LIBRARY_CATEGORIES
const DISPLAY_CATEGORIES = Object.values(HEALTH_AREA_MAPPING);

export const ProgressFeatures = () => {
  const { t, tNode } = useTranslation();
  // Use the exact keyof type expected by ProgressGoalCards prop
  const [categorySlug, setCategorySlug] = useState<string>(
    HEALTH_AREA_MAPPING.nutrition.slug || "nutrition",
  );

  return (
    <section
      id="features"
      data-testid="features"
      className="col-span-12 space-y-8 md:space-y-12 mb-10 md:mb-20"
    >
      <div className="text-center space-y-4">
        <Heading data-testid="features-title" level="h2">
          {t("features.title")}
        </Heading>
        <p className="text-muted-foreground max-w-2xl mx-auto text-pretty text-lg leading-8">
          {t("features.description")}
        </p>
      </div>

      {/* Card 1: Calendar */}
      <Card className="overflow-hidden border shadow-sm">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-2">
          <div className="order-2 md:order-1 space-y-4 p-6 md:p-10 text-center md:text-left">
            <Heading level="h3" size="h5">
              {t("features.calendar.title")}
            </Heading>
            <CardDescription className="text-muted-foreground text-lg leading-tall text-left">
              {tNode("features.calendar.description", {
                healthCalendar: (
                  <span className="font-bold">{t("features.calendar.health_calendar")}</span>
                ),
                healthyActivities: (
                  <span className="font-bold">{t("features.calendar.healthy_activities")}</span>
                ),
                lifelongHealthyHabits: (
                  <span className="font-bold">
                    {t("features.calendar.lifelong_healthy_habits")}
                  </span>
                ),
              })}
              <ul className="mt-4 space-y-2.5">
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <CalendarClock className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.calendar.schedule_routines")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <BarChart3 className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.calendar.identify_patterns")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <Trophy className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.calendar.visualize_progress")}
                  </span>
                </li>
              </ul>
            </CardDescription>
          </div>
          <div className="order-1 md:order-2 h-full flex flex-col items-center justify-center">
            <ProgressCalendar />
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Habit Streaks */}
      <Card className="overflow-hidden border shadow-sm">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-2">
          <div className="order-1 md:order-1 h-full">
            <ProgressStreaksDemo />
          </div>
          <div className="order-2 md:order-2 space-y-4 p-6 md:p-10 text-center md:text-left">
            <Heading level="h3" size="h5">
              {t("features.streaks.title")}
            </Heading>
            <CardDescription className="text-muted-foreground text-lg leading-tall text-left">
              {tNode("features.streaks.description", {
                consistencyStreaks: (
                  <span className="font-bold">{t("features.streaks.consistency_streaks")}</span>
                ),
                lastingHabits: (
                  <span className="font-bold">{t("features.streaks.lasting_habits")}</span>
                ),
              })}
              <ul className="mt-4 space-y-2.5">
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <CalendarClock className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.streaks.track_consistency")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <Trophy className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.streaks.celebrate_milestones")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <BarChart3 className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.streaks.visualize_progress")}
                  </span>
                </li>
              </ul>
            </CardDescription>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Notifications */}
      <Card className="overflow-hidden border shadow-sm">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-2">
          <div className="order-2 md:order-1 space-y-4 p-6 md:p-10 text-center md:text-left">
            <Heading level="h3" size="h5">
              {t("features.notifications.title")}
            </Heading>
            <CardDescription className="text-muted-foreground text-lg leading-tall text-left">
              {tNode("features.notifications.description", {
                consistency: (
                  <span className="font-bold">{t("features.notifications.consistency")}</span>
                ),
                smartNotifications: (
                  <span className="font-bold">
                    {t("features.notifications.smart_notifications")}
                  </span>
                ),
              })}
              <ul className="mt-4 space-y-2.5">
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <Trophy className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.notifications.build_habits")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <CalendarClock className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.notifications.personalized_nudges")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <BarChart3 className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.notifications.milestone_celebrations")}
                  </span>
                </li>
              </ul>
            </CardDescription>
          </div>
          <div className="order-1 md:order-2 h-full flex items-center justify-center">
            <ProgressNotifications />
          </div>
        </CardContent>
      </Card>

      {/* Card 4: Personalized AI Coaching */}
      <Card className="overflow-hidden border shadow-sm">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-2">
          <div className="order-2 md:order-2 space-y-4 p-6 md:p-10 text-center md:text-left">
            <Heading level="h3" size="h5">
              {t("features.ai.title")}
            </Heading>
            <CardDescription className="text-muted-foreground text-lg leading-tall text-left">
              {tNode("features.ai.description", {
                nudgesYou: <span className="font-bold">{t("features.ai.nudges_you")}</span>,
              })}
              <ul className="mt-4 space-y-2.5">
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <RefreshCw className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.ai.creates_routines")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <FlagTriangleRight className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.ai.sets_milestones")}
                  </span>
                </li>
                <li className="w-fit flex items-center gap-2.5 rounded-lg py-2 px-3 bg-primary/[0.03] border-[0.5px] border-primary/[0.07] transition-colors duration-150 hover:bg-primary/[0.06]">
                  <CheckSquare className="size-4 stroke-[1.5] shrink-0" />
                  <span className="text-sm text-foreground/80">
                    {t("features.ai.breaks_down_goals")}
                  </span>
                </li>
              </ul>
            </CardDescription>
          </div>
          <div className="order-1 md:order-1">
            <ProgressAI />
          </div>
        </CardContent>
      </Card>

      {/* Card 5: Health Goals */}
      <Card className="overflow-hidden border shadow-sm">
        <CardContent className="p-0 grid grid-cols-1 lg:grid-cols-2">
          <div className="order-2 md:order-1 space-y-4 p-6 md:p-10 text-center md:text-left">
            <Heading level="h3" size="h5">
              {t("features.goals.title")}
            </Heading>
            <CardDescription className="text-muted-foreground text-lg leading-tall text-left">
              {tNode("features.goals.description", {
                healthGoals: <span className="font-bold">{t("features.goals.health_goals")}</span>,
                dailyProgress: (
                  <span className="font-bold">{t("features.goals.daily_progress")}</span>
                ),
              })}
            </CardDescription>

            {/* Category Badges */}
            <div className="flex flex-wrap gap-2 pt-4 justify-center md:justify-start items-center">
              {DISPLAY_CATEGORIES.map((category) => {
                return (
                  <CategoryBadge
                    key={category.slug}
                    category={category as HealthArea}
                    isSelected={categorySlug === category.slug}
                    onClick={() => setCategorySlug(category.slug || "")}
                  />
                );
              })}
            </div>
          </div>
          <div className="order-1 md:order-2 h-full">
            <ProgressGoalCards categorySlug={categorySlug} />
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
